import { getAriConfig, toAri } from '@atlassian/jira-platform-ari';
import type { QueryResult } from '@atlassian/jira-relay-apollo-migration-utils';
import type {
	perDeploymentMetricsRelayQuery$data,
	perDeploymentMetricsRelayQuery$variables,
} from '@atlassian/jira-relay/src/__generated__/perDeploymentMetricsRelayQuery.graphql';
import { type CloudId, type ProjectId, toIssueId } from '@atlassian/jira-shared-types';
import type { ServiceQueryResultWithFetch } from '@atlassian/jira-software-deployments-service-query-types';
import type { DateRange, Deployment } from '../../common/types';

export const buildQueryVariables = (
	cloudId: CloudId,
	projectId: ProjectId,
	{ startDateInclusive, endDateExclusive }: DateRange,
): perDeploymentMetricsRelayQuery$variables => ({
	filter: {
		cloudId,
		startFromInclusive: startDateInclusive.toISOString(),
		endAtExclusive: endDateExclusive.toISOString(),
		jiraProjectIds: [projectId],
		environmentCategories: ['PRODUCTION'],
	},
});

export const getResourceId = (ariLikeString: string): string => {
	const ari = toAri(ariLikeString);
	if (ari === undefined) {
		throw Error('Invalid ARI');
	}
	return getAriConfig(ari).resourceId;
};

// @ts-expect-error - TS7006 - Parameter 'node' implicitly has an 'any' type.
const transformNode = (node): Deployment => {
	if (node == null || node.deployment == null || node.deploymentSize == null) {
		throw Error('Incomplete deployment node');
	}
	const { deployment, deploymentSize } = node;
	const { id, displayName, url, lastUpdated, issueIds, environment, pipeline } = deployment;
	if (
		id == null ||
		displayName == null ||
		url == null ||
		lastUpdated == null ||
		issueIds == null ||
		environment == null ||
		pipeline == null
	) {
		throw Error('Incomplete deployment node');
	}
	const { displayName: environmentDisplayName } = environment;
	const { displayName: pipelineDisplayName, url: pipelineUrl } = pipeline;
	if (environmentDisplayName == null || pipelineDisplayName == null || pipelineUrl == null) {
		throw Error('Incomplete deployment node');
	}
	return {
		id,
		issueCount: deploymentSize,
		// @ts-expect-error - TS7006 - Parameter 'issueId' implicitly has an 'any' type.
		issueIds: issueIds.map((issueId) => toIssueId(getResourceId(issueId))),
		displayName,
		url,
		// @ts-expect-error - TS2322 - Confusion between JS Date and GraphQL dates encoded as a string
		lastUpdated: new Date(lastUpdated),
		environment: {
			displayName: environmentDisplayName,
		},
		pipeline: {
			url: pipelineUrl,
			displayName: pipelineDisplayName,
		},
	};
};

export const transformResult = (
	queryResult: QueryResult<
		perDeploymentMetricsRelayQuery$variables,
		perDeploymentMetricsRelayQuery$data
	>,
): ServiceQueryResultWithFetch<Deployment[]> => {
	const resultBase = {
		loading: queryResult.loading,
		error: queryResult.error,
		fetch: queryResult.refetch,
	};

	const nodes = queryResult.data?.devOpsMetrics?.perDeploymentMetrics?.nodes;

	if (queryResult.loading || nodes == null) {
		return resultBase;
	}

	try {
		return {
			...resultBase,
			data: nodes.map(transformNode),
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return {
			...resultBase,
			error: queryResult.error ?? error,
		};
	}
};
